import {V1RichContent} from '@wix/ambassador-events-v1-rich-content/types'
import {getAboutText} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import {isEqual} from 'lodash'
import React, {Suspense, useEffect, useMemo, useState} from 'react'
import {truncateRichContent} from 'ricos-content/libs/truncate-rich-content'
import xss from 'xss'
import {getElementStyle} from '../../../../../../commons/utils/dom'
import {useSettings} from '../../../../hooks/settings'
import {useVisibilityStyles} from '../../../../hooks/use-visibility-styles'
import {settingsParams} from '../../../../settingsParams'
import {usePageStateSelector} from '../../../hooks/state'
import {getEvent, getEventRichContent} from '../../../selectors/event'
import {isGroupActivityVisible} from '../../../selectors/groups'
import {isPaidPlansVisible} from '../../../selectors/paid-plans'
import {RicosViewer} from '../../chunks'
import cl from '../../classnames.scss'
import {Subtitle} from '../../subtitle'
import s from './about-section.scss'
import {ReadMoreButton} from './read-more-button'
import {AboutSectionProps} from '.'

const WRAPPER_ID = 'events-details-page-about-wrapper'
const INNER_ID = 'events-details-page-about-inner'

export const AboutSection = ({compact}: AboutSectionProps) => {
  const {isMobile, isEditor} = useEnvironment()
  const {t} = useTranslation()
  const {
    allBreakpoints: {isAboutSectionVisible, isReadMoreButtonVisible},
  } = useVisibilityStyles()
  const paidPlansVisible = usePageStateSelector(state => isPaidPlansVisible(state) && !isGroupActivityVisible(state))
  const eventId = usePageStateSelector(state => getEvent(state).id)
  const aboutText = usePageStateSelector(state => getAboutText(getEvent(state)))
  const richContent = usePageStateSelector(getEventRichContent)
  const settings = useSettings()
  const aboutTitleText = settings.get(settingsParams.aboutTitleText)
  const [collapsed, setCollapsed] = useState(true)
  const [buttonVisible, setButtonVisible] = useState(false)

  const truncatedRichContent = useMemo(() => {
    if (richContent) {
      // @ts-expect-error
      const content = truncateRichContent(richContent, {
        wordCount: 150,
        maxPlugins: 2,
        nodeCount: 6,
      }) as V1RichContent

      return {
        content,
        truncated: !isEqual(content, richContent),
      }
    }
  }, [richContent])

  useEffect(
    () => {
      const wrapper = document.getElementById(WRAPPER_ID)
      const inner = document.getElementById(INNER_ID)

      if (wrapper && inner) {
        setTimeout(() => {
          // todo remove hack and find proper solution
          const wrapperHeight = parseInt(getElementStyle(wrapper, 'height'), 10)
          const innerHeight = parseInt(getElementStyle(inner, 'height'), 10)

          setButtonVisible(innerHeight > wrapperHeight)
        }, 100)
      }
    },
    isEditor ? undefined : [],
  )

  if (!isAboutSectionVisible()) {
    return null
  }

  if (truncatedRichContent) {
    return (
      <div
        data-hook={DH.ABOUT_SECTION}
        className={classNames(s.ricosContainer, {
          [s.ricosMobileContainer]: isMobile,
          [s.largerBottomMargin]: paidPlansVisible,
        })}
      >
        <Subtitle
          dataHook={DH.ABOUT}
          className={classNames({[s.mobileSubtitle]: isMobile})}
          text={aboutTitleText}
          compact={compact}
        />
        <Suspense fallback={null}>
          <RicosViewer content={collapsed && isReadMoreButtonVisible() ? truncatedRichContent.content : richContent} />
        </Suspense>
        <ReadMoreButton
          visible={truncatedRichContent.truncated}
          collapsed={collapsed}
          onClick={() => setCollapsed(!collapsed)}
        />
      </div>
    )
  }

  return (
    <div
      className={classNames(
        s.container,
        {
          [s.mobileContainer]: isMobile,
          [s.largerBottomMargin]: paidPlansVisible,
        },
        cl.evTextFont,
        cl.evTextColor,
      )}
      data-hook={DH.ABOUT_SECTION}
    >
      <Subtitle
        className={classNames({[s.mobileSubtitle]: isMobile})}
        dataHook={DH.ABOUT}
        text={aboutTitleText}
        compact={compact}
      />
      <div
        id={WRAPPER_ID}
        data-hook={DH.ABOUT_SECTION_WRAPPER}
        className={classNames(s.wrapper, {
          [s.expanded]: !collapsed,
          [s.wrapperNoShadow]: !buttonVisible,
          [s.buttonVisible]: buttonVisible,
        })}
      >
        <div
          id={INNER_ID}
          className={s.aboutText}
          data-hook={DH.ABOUT_SECTION_TEXT}
          role="text"
          key={eventId}
          dangerouslySetInnerHTML={{
            __html: xss(isEditor && !aboutText ? t('demoEvent_aboutSectionText') : aboutText),
          }}
        />
      </div>
      <ReadMoreButton visible={buttonVisible} collapsed={collapsed} onClick={() => setCollapsed(!collapsed)} />
    </div>
  )
}
